import type { AdUserAgent, V1AdRequestSignals } from "./types";

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    platform?: string;
    mobile?: boolean;
  };
};

function getNavigator(): NavigatorWithUserAgentData | undefined {
  return globalThis.navigator as NavigatorWithUserAgentData | undefined;
}

function getUserAgent(): string {
  return getNavigator()?.userAgent ?? "";
}

function getPlatform(): string {
  const navigatorRef = getNavigator();

  return navigatorRef?.userAgentData?.platform || navigatorRef?.platform || "";
}

function detectDeviceType(): string {
  const userAgent = getUserAgent().toLowerCase();
  const navigatorRef = getNavigator();
  const maxTouchPoints = navigatorRef?.maxTouchPoints ?? 0;
  const width = globalThis.screen?.width ?? 0;

  if (
    /roku.*tv|smart-tv|smarttv|hbbtv|googletv|tv safari|netcast|viera|tizen.*tv|webos.*tv|playstation|xbox|nintendo/.test(
      userAgent,
    )
  ) {
    return "tv";
  }

  if (/\bstb\b|set[- ]?top[- ]?box|roku|appletv|fire tv stick|chromecast/.test(userAgent)) {
    return "stb";
  }

  if (/ipad|tablet|kindle|silk|playbook/.test(userAgent)) {
    return "tablet";
  }

  if (/android/.test(userAgent) && !/mobile/.test(userAgent)) {
    return "tablet";
  }

  if (/mobi|iphone|ipod|android.*mobile|windows phone/.test(userAgent)) {
    return "mobile";
  }

  if (maxTouchPoints > 1 && width >= 600 && width <= 1366) {
    return "tablet";
  }

  return "website";
}

function detectOs(): string {
  const userAgent = getUserAgent().toLowerCase();
  const platform = getPlatform().toLowerCase();
  const navigatorRef = getNavigator();

  if (
    /iphone|ipad|ipod/.test(userAgent) ||
    (platform === "macintel" && (navigatorRef?.maxTouchPoints ?? 0) > 1)
  ) {
    return "ios";
  }

  if (/android/.test(userAgent)) {
    return "android";
  }

  if (/windows|win32|win64/.test(userAgent) || /win/.test(platform)) {
    return "windows";
  }

  if (/mac os|macintosh/.test(userAgent) || /mac/.test(platform)) {
    return "macos";
  }

  if (/cros|freebsd|openbsd|netbsd|sunos|solaris/.test(`${userAgent} ${platform}`)) {
    return "other";
  }

  if (/linux/.test(userAgent) || /linux/.test(platform)) {
    return "linux";
  }

  return "unknown";
}

function detectBrowser(): string {
  const userAgent = getUserAgent();

  if (/Edg(?:A|iOS)?\//i.test(userAgent)) {
    return "Edge";
  }

  if (/OPR\/|Opera/i.test(userAgent)) {
    return "Opera";
  }

  if (/SamsungBrowser\//i.test(userAgent)) {
    return "Samsung Internet";
  }

  if (/CriOS\/|Chrome\/|Chromium\//i.test(userAgent)) {
    return "Chrome";
  }

  if (/FxiOS\/|Firefox\//i.test(userAgent)) {
    return "Firefox";
  }

  if (/Safari\//i.test(userAgent)) {
    return "Safari";
  }

  return "Unknown";
}

function detectLanguage(): string | undefined {
  const navigatorRef = getNavigator();
  const language = navigatorRef?.languages?.[0] || navigatorRef?.language;
  const languageCode = language?.split(/[-_]/)[0]?.toLowerCase();

  switch (languageCode) {
    case "fa":
      return "farsi";
    case "ps":
      return "pashto";
    case "en":
      return "english";
    default:
      return undefined;
  }
}

function detectTimeOfDay(date = new Date()): string {
  const hour = date.getHours();

  if (hour >= 5 && hour < 12) {
    return "morning";
  }

  if (hour >= 12 && hour < 17) {
    return "afternoon";
  }

  if (hour >= 17 && hour < 21) {
    return "evening";
  }

  return "night";
}

function detectDayOfWeek(date = new Date()): string {
  return [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ][date.getDay()];
}

export function collectAutoSignals(date = new Date()): V1AdRequestSignals {
  const language = detectLanguage();
  const signals: V1AdRequestSignals = {
    device_type: detectDeviceType(),
    os: detectOs(),
    time_of_day: detectTimeOfDay(date),
    day_of_week: detectDayOfWeek(date),
  };

  if (language) {
    signals.language = language;
  }

  return signals;
}

export function collectSignals(
  customSignals: V1AdRequestSignals = {},
): V1AdRequestSignals {
  return {
    ...collectAutoSignals(),
    ...customSignals,
  };
}

export function collectUserAgent(): AdUserAgent {
  return {
    raw: getUserAgent(),
    deviceType: detectDeviceType(),
    os: detectOs(),
    browser: detectBrowser(),
  };
}
